import { deleteMultipartResumeSession, listMultipartResumeSessions } from './multipartResumeStore';

export const DEFAULT_STALE_AFTER_MS = 24 * 60 * 60 * 1000; // 24h
const CLEANUP_THROTTLE_KEY = 'omniscribe_multipart_resume_cleanup_last_ms';
const CLEANUP_THROTTLE_MS = 6 * 60 * 60 * 1000; // 6h
const MAX_CLEANUPS_PER_RUN = 3;

function getNowMs(): number {
  return Date.now();
}

function isAxiosStatus(err: unknown, status: number): boolean {
  if (!err || typeof err !== 'object') return false;
  const maybe = err as { response?: { status?: number } };
  return maybe.response?.status === status;
}

function isAuthError(err: unknown): boolean {
  return isAxiosStatus(err, 401) || isAxiosStatus(err, 403);
}

export async function cleanupStaleMultipartResumeSessions(params: {
  abortMultipartUpload: (fileId: string) => Promise<void>;
  confirmUpload: (fileId: string) => Promise<unknown>;
  staleAfterMs?: number;
}): Promise<{ attempted: number; cleaned: number }> {
  const { abortMultipartUpload, confirmUpload, staleAfterMs = DEFAULT_STALE_AFTER_MS } = params;

  if (typeof localStorage !== 'undefined') {
    const lastRaw = localStorage.getItem(CLEANUP_THROTTLE_KEY);
    const last = lastRaw ? Number(lastRaw) : 0;
    const now = getNowMs();
    if (Number.isFinite(last) && last > 0 && now-last < CLEANUP_THROTTLE_MS) {
      return { attempted: 0, cleaned: 0 };
    }
    try {
      localStorage.setItem(CLEANUP_THROTTLE_KEY, String(now));
    } catch {
      // ignore
    }
  }

  const now = getNowMs();
  const sessions = listMultipartResumeSessions()
    .filter((s) => now - s.updatedAtMs > staleAfterMs)
    .sort((a, b) => b.updatedAtMs - a.updatedAtMs);

  let attempted = 0;
  let cleaned = 0;

  for (const session of sessions.slice(0, MAX_CLEANUPS_PER_RUN)) {
    attempted += 1;
    const hash = session.contentHash;

    // If we already completed multipart but failed to confirm, try confirming again (no S3 "incomplete multipart" risk).
    if (session.needsConfirm) {
      try {
        await confirmUpload(session.fileId);
        deleteMultipartResumeSession(hash);
        cleaned += 1;
      } catch (err) {
        // If we aren't authenticated, keep the session so the right user can recover later.
        if (isAuthError(err)) {
          continue;
        }
        // For missing files or other permanent errors, drop local state.
        if (isAxiosStatus(err, 404)) {
          deleteMultipartResumeSession(hash);
          cleaned += 1;
        }
      }
      continue;
    }

    // For incomplete multipart uploads, attempt to abort server-side to avoid leaving stale multipart state.
    try {
      await abortMultipartUpload(session.fileId);
      deleteMultipartResumeSession(hash);
      cleaned += 1;
    } catch (err) {
      if (isAuthError(err)) {
        continue;
      }
      // If upload doesn't exist anymore, remove local state.
      if (isAxiosStatus(err, 404)) {
        deleteMultipartResumeSession(hash);
        cleaned += 1;
      }
    }
  }

  return { attempted, cleaned };
}

